import { useCallback } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import * as Sentry from '@sentry/nextjs'

import { NotificationType } from '@/graphql/appsync-notifications'
import { useUpdateMySettingMutation } from '@/graphql/generated/hooks'
import { IncidentPriority } from '@/graphql/generated/schemas'
import { isWithinCriticalSoundPolicyGrace } from '@/hooks/criticalSoundPolicyGrace'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectAlwaysPlayCriticalIncidentSound,
  selectIncidentAudioAlert,
  selectIsCriticalSoundPolicyLoaded,
  selectUserSettingsLoaded,
  setIncidentAudioAlert,
} from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { AUDIO_SETTINGS_ANALYTICS } from '../constants/constants'

// TODO - In future make this customizable
export const getIncidentAlertSound = (
  priority?: IncidentPriority | null
): string => {
  switch (priority) {
    case IncidentPriority.Critical:
      return '/audio/critical_incident_notification.mp3'
    default:
      return '/audio/incident_notification.mp3'
  }
}

export const useIncidentAudio = () => {
  const dispatch = useAppDispatch()
  const [updateMySetting, { loading: isUpdatingAudioSetting }] =
    useUpdateMySettingMutation()

  const isFeatureFlagOn = useFeatureFlag(FeatureFlag.incidentAudioAlerts)
  // The organization's "Always play sound for Critical incidents" policy
  // (ENG-35923), kept current in Redux by useCriticalIncidentSoundPolicySync.
  const isCriticalPolicyOn: boolean = useAppSelector(
    selectAlwaysPlayCriticalIncidentSound
  )

  const isAudioAlertOn: boolean = useAppSelector(selectIncidentAudioAlert)
  const isUserSettingsLoaded: boolean = useAppSelector(selectUserSettingsLoaded)

  // Like all incident audio, the policy only does anything where
  // incidentAudioAlerts is on. Turning that flag off must also turn the policy
  // off, or an operator would keep hearing sounds they have no setting to
  // control.
  const isCriticalSoundPolicyActive = isFeatureFlagOn && isCriticalPolicyOn
  // False until the policy has been read from the backend, so explanatory UI
  // doesn't present the not-yet-loaded default as the organization's choice.
  const isCriticalSoundPolicyLoaded: boolean = useAppSelector(
    selectIsCriticalSoundPolicyLoaded
  )

  /**
   * Whether this notification gets a sound regardless of the operator's own
   * audio toggle and the Active Incidents mute: a newly arrived Critical
   * incident while the organization policy is active. Priority belongs to the
   * incident, not the event, so a later clear/flag/audio-message event on the
   * same incident doesn't qualify.
   *
   * Right after login the policy has not been read yet. For that short, bounded
   * grace period it counts as "on", so a Critical incident arriving in the gap
   * is not silent (see criticalSoundPolicyGrace). It does not count once the
   * policy is known, and not after the grace period if it never loaded.
   */
  const isCriticalSoundBypass = useCallback(
    (
      priority?: IncidentPriority | null,
      notificationType?: NotificationType
    ): boolean => {
      const isPolicyAssumedOn =
        isFeatureFlagOn &&
        !isCriticalSoundPolicyLoaded &&
        isWithinCriticalSoundPolicyGrace()
      return (
        (isCriticalSoundPolicyActive || isPolicyAssumedOn) &&
        priority === IncidentPriority.Critical &&
        notificationType === NotificationType.IncidentNew
      )
    },
    [isCriticalSoundPolicyActive, isCriticalSoundPolicyLoaded, isFeatureFlagOn]
  )

  const toggleAudioEnabled = useCallback(async () => {
    const isAudioAlertOnBeforeUpdate = isAudioAlertOn
    const shouldTurnOn = !isAudioAlertOn

    dispatch(setIncidentAudioAlert(shouldTurnOn))

    try {
      const { data } = await updateMySetting({
        variables: {
          input: {
            audibleAlertsEnabled: shouldTurnOn,
          },
        },
      })

      const isAudibleAlertsEnabled =
        data?.updateMySetting?.userSetting?.audibleAlertsEnabled

      if (isAudibleAlertsEnabled !== undefined) {
        dispatch(setIncidentAudioAlert(isAudibleAlertsEnabled))
      }

      trackEvent(AUDIO_SETTINGS_ANALYTICS.toggleAlerts, {
        isNowOn: shouldTurnOn,
      })
    } catch (e) {
      dispatch(setIncidentAudioAlert(isAudioAlertOnBeforeUpdate))
      Sentry.captureException(e)
    }
  }, [dispatch, isAudioAlertOn, updateMySetting])

  /**
   * If the Setting in on AND the feature flag is on, the sound will attempt
   * to be played. A newly-arrived Critical-priority incident bypasses the
   * per-user setting (but not the feature flag) while the organization's
   * Critical-sound policy is on.
   * @return Bool if the sound was successfully played
   */
  const playAlertSound = useCallback(
    async (
      priority?: IncidentPriority | null,
      notificationType?: NotificationType
    ): Promise<boolean> => {
      // Be sure to also check FF! Failing to do so could result in a state where the FF was on, the setting got
      // turned on, then the FF was removed. Now the user will constantly hear the alerts, without the ability to
      // turn them off
      if (!isFeatureFlagOn) {
        return false
      }

      // A bypassed alert ignores the per-user setting entirely, so it shouldn't wait
      // on it either — otherwise a Critical notification arriving before settings
      // finish loading plays no sound at all.
      if (
        !isCriticalSoundBypass(priority, notificationType) &&
        (!isUserSettingsLoaded || !isAudioAlertOn)
      ) {
        return false
      }

      const audioFile = new Audio(getIncidentAlertSound(priority))
      try {
        await audioFile.play()
        return true
      } catch (e) {
        // Be sure to ship error up, but don't have this break anything
        Sentry.captureException(e)
      }

      return false
    },
    [
      isAudioAlertOn,
      isCriticalSoundBypass,
      isFeatureFlagOn,
      isUserSettingsLoaded,
    ]
  )

  return {
    isEnabled: isAudioAlertOn,
    isUpdatingAudioSetting,
    isCriticalSoundPolicyActive,
    isCriticalSoundPolicyLoaded,
    isCriticalSoundBypass,
    toggleAudioEnabled,
    playAlertSound,
  }
}
